// Định nghĩa kiểu theme: "light" = Mặc định, cùng 8 accent màu.
export type Theme =
  | "light"
  | "cobalt"
  | "emerald"
  | "rose"
  | "amber"
  | "slate"
  | "violet"
  | "teal"
  | "crimson";

export const THEME_ORDER: Theme[] = [
  "light",
  "cobalt",
  "emerald",
  "rose",
  "amber",
  "slate",
  "violet",
  "teal",
  "crimson",
];

/** Map id cũ (localStorage) → theme mới tương đương. */
export const LEGACY_THEME_MAP: Record<string, Theme> = {
  dark: "slate",
  ocean: "cobalt",
  forest: "emerald",
  sunset: "amber",
  lavender: "violet",
};

export const migrateLegacyThemeId = (raw: string): Theme | undefined =>
  Object.prototype.hasOwnProperty.call(LEGACY_THEME_MAP, raw)
    ? LEGACY_THEME_MAP[raw]
    : undefined;

/** Theme mặc định — header xanh da trời (#2563eb), nội dung #f1f5f9. */
export const DEFAULT_THEME: Theme = "light";

export const themeLabels: Record<Theme, string> = {
  light: "Mặc định",
  cobalt: "Xanh cobalt",
  emerald: "Ngọc lục bảo",
  rose: "Hồng ruby",
  amber: "Hổ phách",
  slate: "Than chì",
  violet: "Tím thuỷ tinh",
  teal: "Xanh cổ vịt",
  crimson: "Đỏ rượu",
};

/** Ô preview picker (màu accent chính). */
export const themePreviewHex: Record<Theme, string> = {
  light: "#2563eb",
  cobalt: "#2563eb",
  emerald: "#059669",
  rose: "#e11d48",
  amber: "#d97706",
  slate: "#64748b",
  violet: "#7c3aed",
  teal: "#0d9488",
  crimson: "#b91c1c",
};

interface ThemeConfig {
  "--color-gray-dark": string;
  "--color-gray-light": string;
  "--color-blue-dark": string;
  "--color-blue-light": string;
  "--color-blue-hover": string;
  "--color-white": string;
  "--color-black": string;
  "--color-primary": string;
  "--color-disabled-bg": string;
  "--color-disabled-border": string;
  "--color-navbar-bg": string;
  "--color-header-text": string;
  "--color-content-bg": string;
  "--color-menu-normal": string;
  "--color-menu-active": string;
  "--color-menu-hover": string;
  "--color-table-header-bg": string;
  "--color-table-header-text": string;
  "--color-table-row-hover": string;
  "--color-table-border": string;
  "--color-btn-primary": string;
  "--color-btn-primary-hover": string;
  "--color-btn-danger": string;
  "--color-btn-danger-hover": string;
  "--color-btn-default": string;
  "--color-btn-default-hover": string;
  "--color-btn-success": string;
  "--color-btn-success-hover": string;
  "--color-btn-warning": string;
  "--color-btn-warning-hover": string;
  "--color-btn-info": string;
  "--color-btn-info-hover": string;
}

type AccentTone = Pick<
  ThemeConfig,
  | "--color-primary"
  | "--color-navbar-bg"
  | "--color-menu-normal"
  | "--color-menu-active"
  | "--color-menu-hover"
  | "--color-table-header-bg"
  | "--color-table-border"
  | "--color-btn-primary"
  | "--color-btn-primary-hover"
  | "--color-gray-dark"
  | "--color-gray-light"
  | "--color-blue-dark"
  | "--color-blue-light"
  | "--color-blue-hover"
>;

const accentTheme = (a: AccentTone): ThemeConfig => ({
  ...a,
  "--color-white": "#ffffff",
  "--color-black": "#000000",
  "--color-header-text": "#ffffff",
  "--color-content-bg": "#f1f5f9",
  "--color-disabled-bg": "#cbd5e0",
  "--color-disabled-border": "#94a3b8",
  "--color-table-header-text": "#ffffff",
  "--color-table-row-hover": "#f1f5f9",
  "--color-btn-danger": "#dc2626",
  "--color-btn-danger-hover": "#f87171",
  "--color-btn-default": "#808080",
  "--color-btn-default-hover": a["--color-gray-light"],
  "--color-btn-success": "#059669",
  "--color-btn-success-hover": "#10b981",
  "--color-btn-warning": "#d97706",
  "--color-btn-warning-hover": "#f59e0b",
  "--color-btn-info": a["--color-btn-primary"],
  "--color-btn-info-hover": a["--color-btn-primary-hover"],
});

export const themes: Record<Theme, ThemeConfig> = {
  light: {
    "--color-gray-dark": "#1e293b",
    "--color-gray-light": "#3b82f6",
    "--color-blue-dark": "#1d4ed8",
    "--color-blue-light": "#dbeafe",
    "--color-blue-hover": "#60a5fa",
    "--color-white": "#ffffff",
    "--color-black": "#0f172a",
    "--color-primary": "#2563eb",
    "--color-disabled-bg": "#e2e8f0",
    "--color-disabled-border": "#cbd5e1",
    "--color-navbar-bg": "#2563eb",
    "--color-header-text": "#ffffff",
    "--color-content-bg": "#f1f5f9",
    "--color-menu-normal": "#1d4ed8",
    "--color-menu-hover": "#3b82f6",
    "--color-menu-active": "#1e3a8a",
    "--color-table-header-bg": "#2563eb",
    "--color-table-header-text": "#ffffff",
    "--color-table-row-hover": "#f8fafc",
    "--color-table-border": "#e2e8f0",
    "--color-btn-primary": "#2563eb",
    "--color-btn-primary-hover": "#3b82f6",
    "--color-btn-danger": "#dc2626",
    "--color-btn-danger-hover": "#f87171",
    "--color-btn-default": "#64748b",
    "--color-btn-default-hover": "#475569",
    "--color-btn-success": "#059669",
    "--color-btn-success-hover": "#10b981",
    "--color-btn-warning": "#d97706",
    "--color-btn-warning-hover": "#f59e0b",
    "--color-btn-info": "#2563eb",
    "--color-btn-info-hover": "#3b82f6",
  },
  cobalt: accentTheme({
    "--color-primary": "#2563eb",
    "--color-navbar-bg": "#172554",
    "--color-menu-normal": "#1e293b",
    "--color-menu-hover": "#293548",
    "--color-menu-active": "#2563eb",
    "--color-table-header-bg": "#1e3a8a",
    "--color-table-border": "#3b82f6",
    "--color-btn-primary": "#1e40af",
    "--color-btn-primary-hover": "#2563eb",
    "--color-gray-dark": "#0f172a",
    "--color-gray-light": "#334155",
    "--color-blue-dark": "#1d4ed8",
    "--color-blue-light": "#dbeafe",
    "--color-blue-hover": "#3b82f6",
  }),
  emerald: accentTheme({
    "--color-primary": "#059669",
    "--color-navbar-bg": "#022c22",
    "--color-menu-normal": "#064e3b",
    "--color-menu-hover": "#065f46",
    "--color-menu-active": "#059669",
    "--color-table-header-bg": "#065f46",
    "--color-table-border": "#10b981",
    "--color-btn-primary": "#047857",
    "--color-btn-primary-hover": "#059669",
    "--color-gray-dark": "#064e3b",
    "--color-gray-light": "#115e59",
    "--color-blue-dark": "#047857",
    "--color-blue-light": "#d1fae5",
    "--color-blue-hover": "#10b981",
  }),
  rose: accentTheme({
    "--color-primary": "#e11d48",
    "--color-navbar-bg": "#4c0519",
    "--color-menu-normal": "#881337",
    "--color-menu-hover": "#9f1239",
    "--color-menu-active": "#e11d48",
    "--color-table-header-bg": "#9f1239",
    "--color-table-border": "#fb7185",
    "--color-btn-primary": "#be123c",
    "--color-btn-primary-hover": "#e11d48",
    "--color-gray-dark": "#881337",
    "--color-gray-light": "#9f1239",
    "--color-blue-dark": "#be123c",
    "--color-blue-light": "#ffe4e6",
    "--color-blue-hover": "#fb7185",
  }),
  amber: accentTheme({
    "--color-primary": "#d97706",
    "--color-navbar-bg": "#451a03",
    "--color-menu-normal": "#92400e",
    "--color-menu-hover": "#a16207",
    "--color-menu-active": "#d97706",
    "--color-table-header-bg": "#b45309",
    "--color-table-border": "#fbbf24",
    "--color-btn-primary": "#b45309",
    "--color-btn-primary-hover": "#d97706",
    "--color-gray-dark": "#78350f",
    "--color-gray-light": "#92400e",
    "--color-blue-dark": "#b45309",
    "--color-blue-light": "#fef3c7",
    "--color-blue-hover": "#fbbf24",
  }),
  slate: accentTheme({
    "--color-primary": "#64748b",
    "--color-navbar-bg": "#0f172a",
    "--color-menu-normal": "#1e293b",
    "--color-menu-hover": "#334155",
    "--color-menu-active": "#64748b",
    "--color-table-header-bg": "#334155",
    "--color-table-border": "#64748b",
    "--color-btn-primary": "#334155",
    "--color-btn-primary-hover": "#475569",
    "--color-gray-dark": "#0f172a",
    "--color-gray-light": "#334155",
    "--color-blue-dark": "#475569",
    "--color-blue-light": "#f1f5f9",
    "--color-blue-hover": "#94a3b8",
  }),
  violet: accentTheme({
    "--color-primary": "#7c3aed",
    "--color-navbar-bg": "#2e1065",
    "--color-menu-normal": "#5b21b6",
    "--color-menu-hover": "#6d28d9",
    "--color-menu-active": "#7c3aed",
    "--color-table-header-bg": "#5b21b6",
    "--color-table-border": "#a78bfa",
    "--color-btn-primary": "#6d28d9",
    "--color-btn-primary-hover": "#7c3aed",
    "--color-gray-dark": "#4c1d95",
    "--color-gray-light": "#5b21b6",
    "--color-blue-dark": "#6d28d9",
    "--color-blue-light": "#ede9fe",
    "--color-blue-hover": "#a78bfa",
  }),
  teal: accentTheme({
    "--color-primary": "#0d9488",
    "--color-navbar-bg": "#134e4a",
    "--color-menu-normal": "#115e59",
    "--color-menu-hover": "#0f766e",
    "--color-menu-active": "#0d9488",
    "--color-table-header-bg": "#0f766e",
    "--color-table-border": "#2dd4bf",
    "--color-btn-primary": "#0f766e",
    "--color-btn-primary-hover": "#0d9488",
    "--color-gray-dark": "#115e59",
    "--color-gray-light": "#134e4a",
    "--color-blue-dark": "#0f766e",
    "--color-blue-light": "#ccfbf1",
    "--color-blue-hover": "#2dd4bf",
  }),
  crimson: accentTheme({
    "--color-primary": "#b91c1c",
    "--color-navbar-bg": "#450a0a",
    "--color-menu-normal": "#7f1d1d",
    "--color-menu-hover": "#991b1b",
    "--color-menu-active": "#b91c1c",
    "--color-table-header-bg": "#991b1b",
    "--color-table-border": "#f87171",
    "--color-btn-primary": "#991b1b",
    "--color-btn-primary-hover": "#b91c1c",
    "--color-gray-dark": "#7f1d1d",
    "--color-gray-light": "#991b1b",
    "--color-blue-dark": "#991b1b",
    "--color-blue-light": "#fee2e2",
    "--color-blue-hover": "#f87171",
  }),
};

export const buttonDisabledStyles = {
  opacity: 0.2,
};
